/**
 * PeriodSelector — dropdown for fiscal calendar periods.
 * Fetches from GET /api/v1/org/fiscal-calendar and displays period names.
 */

import React, { useState, useEffect } from 'react';
import { BASE, authFetch } from '../constants';

export interface FiscalPeriod {
  period_number: number;
  period_code: string;
  period_name: string;
  start_date: string;
  end_date: string;
  is_closed: boolean;
  is_adjustment: boolean;
}

interface PeriodSelectorProps {
  value: string;
  onChange: (periodCode: string) => void;
  fiscalYear?: number;
  defaultToPrior?: boolean;
  onPeriodsLoaded?: (periods: FiscalPeriod[]) => void;
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  value, onChange, fiscalYear, defaultToPrior, onPeriodsLoaded,
}) => {
  const [periods, setPeriods] = useState<FiscalPeriod[]>([]);

  useEffect(() => {
    const fy = fiscalYear || new Date().getFullYear();
    authFetch(`${BASE}/api/v1/org/fiscal-calendar?fiscal_year=${fy}`)
      .then(r => r.json())
      .then(data => {
        const list: FiscalPeriod[] = Array.isArray(data) ? data : [];
        setPeriods(list);
        if (onPeriodsLoaded) onPeriodsLoaded(list);

        // Auto-select if no value set
        if (!value && list.length > 0) {
          const today = new Date().toISOString().slice(0, 10);
          const regularPeriods = list.filter(p => !p.is_adjustment);
          const currentIdx = regularPeriods.findIndex(
            p => p.start_date <= today && p.end_date >= today
          );

          if (defaultToPrior) {
            // Select the period before current
            const priorIdx = currentIdx > 0 ? currentIdx - 1 : Math.max(0, regularPeriods.length - 2);
            if (regularPeriods[priorIdx]) {
              onChange(regularPeriods[priorIdx].period_code);
            }
          } else {
            // Select current period
            if (currentIdx >= 0) {
              onChange(regularPeriods[currentIdx].period_code);
            }
          }
        }
      })
      .catch(() => {});
  }, [fiscalYear]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: 'var(--bg-elevated, var(--bg-elevated))',
        color: 'var(--text-primary, #F0F2F5)',
        border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))',
        borderRadius: 'var(--radius-sm, 6px)',
        padding: '6px 10px',
        fontSize: '12px',
        fontFamily: "var(--font-body, var(--font-sans))",
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      {periods.length === 0 && <option value="">Loading...</option>}
      {periods.map(p => (
        <option key={p.period_code} value={p.period_code}>
          {p.period_name}{p.is_closed ? ' (Closed)' : ''}{p.is_adjustment ? ' (Adj)' : ''}
        </option>
      ))}
    </select>
  );
};

export default PeriodSelector;
